import { differenceInDays, parseISO, startOfDay } from 'date-fns';

export type HmrcDeadlineType = 'self-assessment' | 'payment-on-account' | 'vat-return' | 'other';
export type HmrcReminderPhase = 'future' | '1-month' | '2-week' | '3-day' | 'missed';

type DeadlineSupport = {
  label: string;
  defaultTitle: string;
  summary: string;
  linkLabel: string;
  linkUrl: string;
  actions: Record<HmrcReminderPhase, string>;
};

export const HMRC_DEADLINE_SUPPORT: Record<HmrcDeadlineType, DeadlineSupport> = {
  'self-assessment': {
    label: 'Self Assessment',
    defaultTitle: 'Self Assessment tax return',
    summary: 'File the tax return and settle what is due by this date. If your income has changed a lot, check the figures before the window gets tight.',
    linkLabel: 'Open Self Assessment',
    linkUrl: 'https://www.gov.uk/self-assessment-tax-returns',
    actions: {
      future: 'No nudge yet. Keep your records tidy so the 1 month reminder is easier to act on.',
      '1-month': 'Pull together income, expenses, pension, and gift-aid figures now so the return is not a late scramble.',
      '2-week': 'Draft the return, sense-check what is due, and ring-fence the payment if you can.',
      '3-day': 'Submit and pay now, leaving time for HMRC login, banking, or transfer delays.',
      missed: 'Submit and pay as soon as you can. Penalties and interest can start once the deadline passes.',
    },
  },
  'payment-on-account': {
    label: 'Payment on account',
    defaultTitle: 'Payment on account',
    summary: 'This is an advance payment toward the next tax bill. Review it early if the current year looks lower than the one HMRC used.',
    linkLabel: 'Open payment on account guidance',
    linkUrl: 'https://www.gov.uk/understand-self-assessment-bill/payments-on-account',
    actions: {
      future: 'No nudge yet. Keep cash set aside so the instalment does not land as a shock later.',
      '1-month': 'Check whether this year looks lower than HMRC assumed and decide if you need to review the amount.',
      '2-week': 'Confirm the figure in your HMRC account and make sure the payment cash is ready.',
      '3-day': 'Pay now so the money reaches HMRC in time and does not slip past the deadline.',
      missed: 'Pay as soon as you can to limit interest building up on the missed instalment.',
    },
  },
  'vat-return': {
    label: 'VAT return',
    defaultTitle: 'VAT return and payment',
    summary: 'File the return and make sure the VAT payment is sorted by the same deadline.',
    linkLabel: 'Open VAT returns',
    linkUrl: 'https://www.gov.uk/vat-returns',
    actions: {
      future: 'No nudge yet. Keep invoices, receipts, and VAT codes tidy so the quarter is easier to close.',
      '1-month': 'Pull the quarter together now and check sales, purchases, and VAT coding before the deadline gets close.',
      '2-week': 'Draft the return, sense-check the totals, and make sure the VAT payment cash is available.',
      '3-day': 'Submit the VAT return and pay now, leaving time for HMRC or banking delays.',
      missed: 'File and pay as soon as you can to limit penalty, surcharge, or interest risk.',
    },
  },
  other: {
    label: 'Other HMRC',
    defaultTitle: 'HMRC deadline',
    summary: 'Keep the official HMRC page close if this date affects filing, payment, or year-end admin.',
    linkLabel: 'Open HMRC online account',
    linkUrl: 'https://www.gov.uk/log-in-register-hmrc-online-services',
    actions: {
      future: 'No nudge yet. Keep the paperwork close so the next reminder is easier to act on.',
      '1-month': 'Work out exactly what this date needs and pull the paperwork together now.',
      '2-week': 'Double-check what must be filed or paid and leave time for follow-up if something is unclear.',
      '3-day': 'Finish the filing or payment now so nothing slips at the last minute.',
      missed: 'Take the next step with HMRC as soon as you can rather than letting the delay drift.',
    },
  },
};

export const HMRC_DEADLINE_TYPE_OPTIONS: Array<{ value: HmrcDeadlineType; label: string }> = [
  { value: 'self-assessment', label: 'Self Assessment' },
  { value: 'payment-on-account', label: 'Payment on account' },
  { value: 'vat-return', label: 'VAT return' },
  { value: 'other', label: 'Other HMRC' },
];

export function getHmrcReminderPhase(dateStr: string, now = new Date()): HmrcReminderPhase {
  const days = differenceInDays(parseISO(dateStr), startOfDay(now));

  if (days < 0) return 'missed';
  if (days <= 3) return '3-day';
  if (days <= 14) return '2-week';
  if (days <= 30) return '1-month';
  return 'future';
}

export function getHmrcReminderWindowLabel(dateStr: string, now = new Date()) {
  const phase = getHmrcReminderPhase(dateStr, now);

  if (phase === 'missed') return 'Missed';
  if (phase === '3-day') return '3-day window';
  if (phase === '2-week') return '2-week window';
  if (phase === '1-month') return '1-month window';
  return 'Next reminder at 1 month out';
}

export function getHmrcDeadlineAction(type: string, dateStr: string, now = new Date()) {
  const support = HMRC_DEADLINE_SUPPORT[(type as HmrcDeadlineType)] || HMRC_DEADLINE_SUPPORT.other;
  const phase = getHmrcReminderPhase(dateStr, now);
  return support.actions[phase];
}
